import { TableCellStyled, TableSortLabelStyled } from "./table-sort.styles";
import { Box, Table, TableCell, TableHead, TablePagination, TableRow, Tooltip, useMediaQuery } from "@mui/material";
import { screenBreackpoints } from "../../theme";
import React from "react";
import { Column, Order } from "./table-sort.types";
import { useTranslation } from "react-i18next";
import { sortData } from "./table-sort.functions";

interface Props<T> {
  data: T[];
  columns: Column<T>[];
  sortDefault: keyof T;
  renderBody: (data: T[]) => React.ReactNode;
  visibleRowsOptions?: number[];
  nOptionVisible?: number;
}

export const TableSort = <T,>({
  data,
  columns,
  sortDefault,
  renderBody,
  visibleRowsOptions = [10, 20, 40],
  nOptionVisible = 0
}: Props<T>) => {
  const isMobile = useMediaQuery(`(max-width:${screenBreackpoints.mobile})`);
  const [order, setOrder] = React.useState<Order>(Order.DESCENDING);
  const [orderBy, setOrderBy] = React.useState<keyof T>(sortDefault);
  const rowsNumberOptions = React.useMemo(() => {
    const options = visibleRowsOptions.filter(n => n < data.length);
    if (!options.includes(data.length)) {
      options.push(data.length);
    }
    return options;
  }, [data.length, visibleRowsOptions]);
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsNumberOptions[nOptionVisible]);
  const [page, setPage] = React.useState(0);
  const { t } = useTranslation();

  const sortColumn = (
    columnKey: keyof T,
  ) => {
    const newOrder = orderBy === columnKey
      ? (order === Order.ASCENDING ? Order.DESCENDING : Order.ASCENDING)
      : Order.DESCENDING;
    setOrder(newOrder);
    setOrderBy(columnKey);
  };

  const changePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const changeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleStats = React.useMemo(
    () =>
      sortData(data, orderBy, order).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, data],
  );

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => column.empty ? (
              <TableCell key={column.key} />
            ) : (
              <TableCellStyled key={column.key} isMobile={isMobile}>
                <TableSortLabelStyled
                  active={orderBy === column.key}
                  direction={order}
                  onClick={() => sortColumn(column.key)}
                  role="button"
                  aria-label={t("COMPONENTS.TABLE_SORT.ARIA_SORT") + column.title}
                >
                  <Tooltip title={column.tooltip} placement="top" enterDelay={800}>
                    <Box>{column.title}</Box>
                  </Tooltip>
                </TableSortLabelStyled>
              </TableCellStyled>
            ))}
          </TableRow>
        </TableHead>
        {renderBody(visibleStats)}
      </Table>
      {data.length > visibleRowsOptions[0] &&
        <TablePagination
          rowsPerPageOptions={rowsNumberOptions}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={changePage}
          onRowsPerPageChange={changeRowsPerPage}
        />
      }
    </>
  );
}
